import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LoggingService } from '../../../logging/logging.service';
import { UserPreferencesService } from '../../../user/services/user-preferences/user-preferences.service';
import { User } from '../../../user/entities/user';
import { UserRoles } from '../../../user/entities/user-roles';
import {
    CurafidaMode,
    EmailNotificationScope,
    FrontendSetting,
    UserPreferencesResource,
} from '../../../user/entities/user-preferences/user-preferences-resource';
import { CurafidaCardComponent } from '../../../common/components/curafida-card/curafida-card.component';
import { disabled, form, FormField } from '@angular/forms/signals';
import { SelectInputItem } from '../../../common/components/curafida-input/select-input-item';
import { IonCardContent, IonCheckbox, IonItem, IonLabel, IonRange } from '@ionic/angular';
import { CurafidaSaveCancelButtonComponent } from '../../../common/components/curafida-save-cancel-button/curafida-save-cancel-button.component';
import { ThemeService } from '../../../common/services/theme/theme.service';
import { ConfigService } from '../../services/config.service';
import { RoutingSegment } from '../../../common/entities/routing-segment';
import { Router } from '@angular/router';
import { StyleService } from '../../../common/services/style/style.service';
import { TranslatePipe } from '@ngx-translate/core';
import { CurafidaAuthService } from '../../../authentication/curafida-auth.service';
import { ToastService } from '../../../common/services/toast-service/toast-service.service';
import { IonicColor } from '../../../common/entities/toast/ionic-color';
import { UserPreferencesDto } from '../../../user/entities/user-preferences/user-preferences.dto';
import { CurafidaSelectFieldComponent } from '../../../common/components/curafida-input/curafida-select-field/curafida-select-field.component';

/** Nur diese Rollen erhalten E-Mail-Benachrichtigungen und damit auch die Einstellungen dazu. */
const EMAIL_NOTIFICATION_ROLES = [UserRoles.PATIENT, UserRoles.CAREGIVER, UserRoles.SUPERVISOR];

const PATIENT_MODE_LABELS: Record<CurafidaMode, string> = {
    DEFAULT: 'CONFIGURATION.PATIENT.DEFAULT_MODE',
    MINI: 'MINIMAL_MODE',
    KIOSK: 'KIOSK.SINGULAR',
};

/** Die bearbeitbaren Werte der Einstellungen; das Modell des Signal-Formulars. */
export interface UserPreferencesFormValue {
    currentMode: CurafidaMode;
    fontSize: number;
    course: boolean;
    announcementNotifications: boolean;
    chatNotifications: EmailNotificationScope;
    taskStartNotifications: EmailNotificationScope;
    taskDueDateNotifications: EmailNotificationScope;
    taskFinishedDateNotifications: EmailNotificationScope;
    termineStartNotifications: EmailNotificationScope;
    termineChangeNotifications: EmailNotificationScope;
    trainingStartNotifications: EmailNotificationScope;
    trainingEndNotifications: EmailNotificationScope;
    newPatientAssignedNotifications: boolean;
    newPatientNotifications: boolean;
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'user-preferences',
    templateUrl: './user-preferences.component.html',
    styleUrls: ['./user-preferences.component.scss'],
    imports: [
        CurafidaCardComponent,
        CurafidaSelectFieldComponent,
        IonCardContent,
        CurafidaSaveCancelButtonComponent,
        IonLabel,
        IonRange,
        TranslatePipe,
        IonCheckbox,
        IonItem,
        FormField,
        CurafidaSelectFieldComponent,
    ],
})
export class UserPreferencesComponent implements OnInit {
    readonly user = input<User>();
    protected readonly resource = signal<UserPreferencesResource>(undefined);
    protected readonly isEditEnabled = signal(false);
    protected readonly isMobile = inject(StyleService).isMobile;
    protected readonly log = inject(LoggingService).getLogger(this.constructor.name);
    protected readonly userPreferencesService = inject(UserPreferencesService);
    protected readonly configService = inject(ConfigService);
    protected readonly UserRoles = UserRoles;
    protected readonly model = signal<UserPreferencesFormValue>({
        currentMode: undefined,
        fontSize: 14,
        course: false,
        announcementNotifications: true,
        chatNotifications: EmailNotificationScope.ALL,
        taskStartNotifications: EmailNotificationScope.ALL,
        taskDueDateNotifications: EmailNotificationScope.ALL,
        taskFinishedDateNotifications: EmailNotificationScope.ALL,
        termineStartNotifications: EmailNotificationScope.ALL,
        termineChangeNotifications: EmailNotificationScope.ALL,
        trainingStartNotifications: EmailNotificationScope.ME,
        trainingEndNotifications: EmailNotificationScope.ME,
        newPatientAssignedNotifications: true,
        newPatientNotifications: true,
    });
    /* Eine Regel auf der Wurzel genügt: `disabled` wird an alle Kindfelder vererbt. */
    protected readonly preferencesForm = form(this.model, (path) => {
        disabled(path, { when: () => !this.isEditEnabled() });
    });
    /**
     * Patienten, Betreuer und Supervisoren erhalten E-Mail-Benachrichtigungen; für reine
     * Benutzer- und Katalogverwalter sowie Analysten gibt es keine, deshalb werden für sie
     * auch keine Einstellungen gespeichert.
     */
    protected readonly showEmailNotificationScope = computed(
        () => this.user()?.roles?.some((role) => EMAIL_NOTIFICATION_ROLES.includes(role)) ?? false,
    );
    protected readonly patientEmailNotificationScopes: SelectInputItem[] = [
        new SelectInputItem(EmailNotificationScope.ME, 'USER_PREFERENCES.EMAIL_NOTIFICATION_SCOPE.ME_PATIENT'),
        new SelectInputItem(EmailNotificationScope.NONE, 'USER_PREFERENCES.EMAIL_NOTIFICATION_SCOPE.NONE'),
    ];
    protected readonly careEmailNotificationScopes: SelectInputItem[] = [
        new SelectInputItem(EmailNotificationScope.ALL, 'USER_PREFERENCES.EMAIL_NOTIFICATION_SCOPE.ALL_CARE_USER'),
        new SelectInputItem(EmailNotificationScope.ME, 'USER_PREFERENCES.EMAIL_NOTIFICATION_SCOPE.ME_CARE_USER'),
        new SelectInputItem(EmailNotificationScope.NONE, 'USER_PREFERENCES.EMAIL_NOTIFICATION_SCOPE.NONE'),
    ];
    /** Supervisoren betreuen immer nur die ihnen zugeordneten Patienten; „alle" gibt es für sie nicht. */
    protected readonly supervisorEmailNotificationScopes: SelectInputItem[] = [
        new SelectInputItem(EmailNotificationScope.ME, 'USER_PREFERENCES.EMAIL_NOTIFICATION_SCOPE.ME_CARE_USER'),
        new SelectInputItem(EmailNotificationScope.NONE, 'USER_PREFERENCES.EMAIL_NOTIFICATION_SCOPE.NONE'),
    ];
    protected readonly booleanScope: SelectInputItem[] = [
        new SelectInputItem(false, 'DISABLED'),
        new SelectInputItem(true, 'ENABLE_STATE'),
    ];
    protected readonly User = User;
    private readonly themeService = inject(ThemeService);
    private readonly router = inject(Router);
    private readonly authService = inject(CurafidaAuthService);
    private readonly toastService = inject(ToastService);
    /**
     * Wie `UserPreferencesService`: Patienten nutzen nur die freigegebenen Modi (ohne Konfiguration
     * MINI) und den Kiosk; alle anderen Rollen sind immer im Modus DEFAULT.
     */
    protected readonly patientMode = computed(() => {
        const enabledModus = this.configService.config()?.configuration.features.user?.patient?.enabledModus ?? [
            'MINI',
        ];
        const modes = enabledModus
            .filter((mode) => mode !== 'KIOSK')
            .map((mode) => new SelectInputItem(mode, PATIENT_MODE_LABELS[mode]));
        if (this.configService.frontendConfig().features?.kiosk?.enabled) {
            modes.push(new SelectInputItem('KIOSK', PATIENT_MODE_LABELS.KIOSK));
        }
        return modes;
    });

    async ngOnInit() {
        await this.initResource();
    }

    async initResource(): Promise<void> {
        try {
            const resource = await this.userPreferencesService.getForUser(this.user().username);
            /* Nur ergänzen, nicht ersetzen: sonst ginge eine gespeicherte Schriftgröße verloren. */
            resource.frontendSettings ??= new FrontendSetting();
            resource.frontendSettings.enabledSegments ??= [];
            const settings = resource.emailNotificationSettings;
            /* „Alle Patienten" gibt es nur für Betreuer; alle anderen könnten den Wert gar nicht wählen. */
            const defaultScope = this.setItemList()?.some((item) => item.value === EmailNotificationScope.ALL)
                ? EmailNotificationScope.ALL
                : EmailNotificationScope.ME;
            this.model.set({
                currentMode: resource.currentMode,
                fontSize: resource.frontendSettings?.fontSize ?? 14,
                course: !!resource.frontendSettings.enabledSegments?.find((x) => x.name === 'course')?.status,
                /* Ein älteres Backend kennt die Felder noch nicht; dann gilt das bisherige Verhalten. */
                announcementNotifications: settings?.announcementNotifications ?? true,
                chatNotifications: settings?.chatNotifications ?? defaultScope,
                taskStartNotifications: settings?.taskStartNotifications ?? defaultScope,
                taskDueDateNotifications: settings?.taskDueDateNotifications ?? defaultScope,
                taskFinishedDateNotifications: settings?.taskFinishedDateNotifications ?? defaultScope,
                termineStartNotifications: settings?.termineStartNotifications ?? defaultScope,
                termineChangeNotifications: settings?.termineChangeNotifications ?? defaultScope,
                newPatientNotifications: settings?.newPatientNotifications ?? true,
                newPatientAssignedNotifications: settings?.newPatientAssignedNotifications ?? true,
                trainingStartNotifications: settings?.trainingStartNotifications ?? EmailNotificationScope.ME,
                trainingEndNotifications: settings?.trainingEndNotifications ?? EmailNotificationScope.ME,
            });
            /* Setzt nur `dirty`/`touched` zurück, nicht das Modell: frisch geladene Werte sind
             * keine Benutzereingabe, sonst bliebe "Speichern" nach "Abbrechen" aktiv. */
            this.preferencesForm().reset();
            this.resource.set(resource);
        } catch (err) {
            this.log.error((err as Error).message);
        }
    }

    /**
     * Changes the text size based on user input and updates the theme service.
     */
    changeTextSize(size: { detail: { value: number } }) {
        this.resource.update((resource) => {
            const frontendSettings = resource.frontendSettings ?? new FrontendSetting();
            frontendSettings.fontSize = size.detail.value ?? 14;
            return { ...resource, frontendSettings };
        });
        if (this.user().username === this.authService.loggedInUser().username) {
            this.themeService.setTextSize(size.detail.value ?? 14);
        }
    }

    setItemList() {
        if (this.isPatient()) {
            return this.patientEmailNotificationScopes;
        } else if (this.user().roles.includes(UserRoles.CAREGIVER)) {
            return this.careEmailNotificationScopes;
        } else if (this.user().roles.includes(UserRoles.SUPERVISOR)) {
            return this.supervisorEmailNotificationScopes;
        }
    }

    protected async saveForm() {
        const value = this.model();
        /* Auf einer Kopie arbeiten, damit nach einem Fehler der zuletzt gespeicherte Stand bleibt. */
        const resource = structuredClone(this.resource());
        resource.currentMode = value.currentMode;
        resource.frontendSettings.fontSize = value.fontSize;
        if (!this.isPatient()) {
            const segments = resource.frontendSettings.enabledSegments;
            const course = segments.find((x) => x.name === 'course');
            if (course) {
                course.status = value.course;
            } else {
                segments.push({ name: 'course', status: value.course });
            }
        }
        if (this.showEmailNotificationScope()) {
            /* Neu aufbauen statt mutieren: gegen ein älteres Backend fehlt das Objekt. */
            resource.emailNotificationSettings = {
                announcementNotifications: this.isPatient() ? value.announcementNotifications : null,
                chatNotifications: value.chatNotifications,
                taskStartNotifications: value.taskStartNotifications,
                taskDueDateNotifications: value.taskDueDateNotifications,
                taskFinishedDateNotifications: value.taskFinishedDateNotifications,
                termineStartNotifications: value.termineStartNotifications,
                termineChangeNotifications: value.termineChangeNotifications,
                newPatientAssignedNotifications: value.newPatientAssignedNotifications,
                newPatientNotifications: value.newPatientNotifications,
                trainingStartNotifications: value.trainingStartNotifications,
                trainingEndNotifications: value.trainingEndNotifications,
            };
        }
        try {
            const saved = await this.userPreferencesService.updateForUser(
                this.user().username,
                UserPreferencesDto.fromResource(resource),
            );
            /* `patch` meldet einen Fehler nicht, sondern liefert `undefined`. */
            if (!saved) {
                throw new Error('Could not save user preferences');
            }
            this.resource.set(saved);
            this.preferencesForm().reset();
            this.isEditEnabled.set(false);
        } catch (err) {
            /* Im Bearbeitungsmodus bleiben, damit die Eingaben nicht verloren gehen. */
            this.log.error((err as Error).message);
            this.toastService.showToast('USER_PREFERENCES.SAVE_ERROR', IonicColor.danger);
            return;
        }

        if (
            this.resource()?.currentMode === 'KIOSK' &&
            this.authService.loggedInUser().roles.includes(UserRoles.PATIENT)
        ) {
            await this.router.navigate([RoutingSegment.MEMBER, RoutingSegment.KIOSK]);
        }
    }

    protected toggleEdit() {
        this.isEditEnabled.update((isEditEnabled) => !isEditEnabled);
    }

    protected async cancelForm(): Promise<void> {
        this.toggleEdit();
        await this.initResource();
    }

    protected isPatient() {
        return this.user().roles.includes(UserRoles.PATIENT);
    }

    protected isCareUser() {
        return this.user().roles.includes(UserRoles.CAREGIVER) || this.user().roles.includes(UserRoles.SUPERVISOR);
    }
}
